// sideBar/sideBar.tsx
import React, {JSX} from 'react';
import {Link, useLocation} from 'react-router-dom';
import clsx from 'clsx';
import {IconTree, MenuConfig} from './menuConfig';

interface SidebarProps {
  className?: string;
  menu: MenuConfig;
  handleClick: any;
}

// Icon Renderer
const RenderIcon: React.FC<{icons: IconTree[]}> = ({icons}) => {
  return (
    <span className="inline-block">
      {icons.map((icon, i) => {
        const Tag = icon.tag as keyof JSX.IntrinsicElements;
        return (
          <Tag key={i} {...icon.attr}>
            {icon.child?.map((child, j) => {
              const ChildTag = child.tag as keyof JSX.IntrinsicElements;
              return <ChildTag key={j} {...child.attr} />;
            })}
          </Tag>
        );
      })}
    </span>
  );
};

export const CustomMenu: React.FC<{
  menu: MenuConfig;
  orientation?: 'vertical' | 'horizontal';
  gap?: string;
  handleClick: (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => void;
}> = ({menu, orientation = 'vertical', gap = 'gap-0', handleClick}) => {
  const location = useLocation();

  return (
    <ul
      className={clsx(
        'text-sm text-muted overflow-hidden flex',
        orientation === 'vertical' ? 'flex-col items-start' : 'flex-row',
        gap,
      )}
    >
      {menu.items.map(item => {
        const isActive = location.pathname === item.action;
        return (
          <li
            key={item.id}
            className={clsx(
              'block w-full my-4 px-6 rounded outline-none focus-visible:ring-2 whitespace-nowrap transition-all duration-200',
              isActive
                ? 'bg-gradient-to-r from-brand-purple-light to-brand-pink-light text-brand-purple font-semibold border-r-4 border-brand-purple'
                : 'hover:bg-gray-50 text-gray-600'
            )}
          >
            {item.type === 'route' ? (
              <Link
                onClick={handleClick}
                to={item.action}
                target={item.target || '_self'}
                className="flex items-center justify-start gap-10 w-full py-10"
              >
                {item.icon && (
                  <div className="w-20 h-20">
                    <RenderIcon icons={item.icon} />
                  </div>
                )}
                <span className="overflow-hidden text-ellipsis">
                  {item.label}
                </span>
              </Link>
            ) : (

              <a  href={item.action}
                target={item.target || '_self'}
                className="flex items-center justify-start gap-10 w-full py-10"
              >
                {item.icon && (
                  <div className="w-20 h-20">
                    <RenderIcon icons={item.icon} />
                  </div>
                )}
                <span className="overflow-hidden text-ellipsis">
                  {item.label}
                </span>
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
};

// Sidebar Component
export const Sidebar: React.FC<SidebarProps> = ({
  className,
  menu,
  handleClick,
}) => {
  return (
    <div
      className={clsx(
        className,
        'flex flex-col gap-20 border-r bg-alt text-sm font-medium text-muted h-full',
      )}
    >
      <div className="flex-auto">
        <div className="text-muted mt-26 px-12">
          <CustomMenu
            menu={menu}
            orientation="vertical"
            gap="gap-0"
            handleClick={handleClick}
          />
        </div>
      </div>
    </div>
  );
};
